/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { adminApi, formatErr } from "./api";
import { useEscapeKey } from "./hooks";
import { toast } from "sonner";
import { ArrowDownToLine, ArrowUpFromLine, AlertOctagon, RotateCcw, Plus, X, Lock, Pencil, CheckCircle2, History } from "lucide-react";

const MOVEMENT_TYPES = [
  { value: "stock_in", label: "Stock in", icon: ArrowDownToLine, accent: "text-emerald-400" },
  { value: "stock_out", label: "Stock out", icon: ArrowUpFromLine, accent: "text-rose-300" },
  { value: "damaged", label: "Damaged", icon: AlertOctagon, accent: "text-amber-400" },
  { value: "return", label: "Return", icon: RotateCcw, accent: "text-sky-400" },
];

const empty = {
  sku: "", movement_type: "stock_in", quantity: 0,
  source_type: "manufacturer", reference: "", warehouse_id: "", batch_id: "",
  distributor_id: "", notes: "",
};

const emptyEdit = {
  source_type: "manufacturer", reference: "", warehouse_id: "", batch_id: "",
  distributor_id: "", notes: "",
};

export default function AdminInventory() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [movements, setMovements] = useState([]);
  const [filter, setFilter] = useState("");
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(emptyEdit);
  const [editBusy, setEditBusy] = useState(false);
  const [finalizing, setFinalizing] = useState(false);

  // ESC-to-close — innermost first
  useEscapeKey(editOpen, () => setEditOpen(false));
  useEscapeKey(open, () => setOpen(false));
  useEscapeKey(!!detail && !editOpen, () => setDetail(null));

  const load = async () => {
    const [p, w, d, m] = await Promise.all([
      adminApi.get("/products"),
      adminApi.get("/warehouses"),
      adminApi.get("/distributors"),
      adminApi.get("/inventory/movements", { params: { limit: 300 } }),
    ]);
    setProducts(p.data.products);
    setWarehouses(w.data.warehouses);
    setDistributors(d.data.distributors);
    setMovements(m.data.movements);
  };
  useEffect(() => { load(); }, []);

  // React to query-string deep links from Dashboard
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const wantsLowStock = searchParams.get("lowStock") === "1";
      const skuParam = searchParams.get("sku");
      const restock = searchParams.get("restock") === "1";
      if (!wantsLowStock && !(skuParam && restock)) return;
      await Promise.resolve();
      if (cancelled) return;
      if (wantsLowStock) setLowStockOnly(true);
      if (skuParam && restock) {
        setForm({ ...empty, sku: skuParam, movement_type: "stock_in", quantity: 0 });
        setOpen(true);
        const next = new URLSearchParams(searchParams);
        next.delete("sku");
        next.delete("restock");
        setSearchParams(next, { replace: true });
      }
    })();
    return () => { cancelled = true; };
  }, [searchParams, setSearchParams]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const activeDistributors = distributors.filter((d) => d.active !== false);
  const distributorById = Object.fromEntries(distributors.map((d) => [d.id, d.name]));

  const save = async (e) => {
    e.preventDefault();
    if (form.source_type === "distributor" && !form.distributor_id) {
      toast.error("Please select a registered distributor");
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form, quantity: Number(form.quantity) };
      if (!payload.warehouse_id) delete payload.warehouse_id;
      if (!payload.batch_id) delete payload.batch_id;
      if (!payload.notes) delete payload.notes;
      if (payload.source_type !== "distributor" || !payload.distributor_id) delete payload.distributor_id;
      if (!payload.reference) delete payload.reference;
      await adminApi.post("/inventory/movements", payload);
      toast.success("Movement recorded");
      setOpen(false);
      setForm(empty);
      await load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const filtered = filter ? movements.filter((m) => m.sku === filter) : movements;

  const openEditDialog = (m) => {
    if (m.finalized) { toast.error("Movement is finalised — locked"); return; }
    setDetail(m);
    setEditForm({
      source_type: m.source_type || "manufacturer",
      reference: m.reference || "",
      warehouse_id: m.warehouse_id || "",
      batch_id: m.batch_id || "",
      distributor_id: m.distributor_id || "",
      notes: m.notes || "",
    });
    setEditOpen(true);
  };
  const editSet = (k) => (e) => setEditForm({ ...editForm, [k]: e.target.value });

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!detail) return;
    if (editForm.source_type === "distributor" && !editForm.distributor_id) {
      toast.error("Please select a registered distributor");
      return;
    }
    setEditBusy(true);
    try {
      const payload = { ...editForm };
      if (!payload.warehouse_id) delete payload.warehouse_id;
      if (!payload.batch_id) delete payload.batch_id;
      if (!payload.notes) delete payload.notes;
      if (!payload.reference) delete payload.reference;
      if (payload.source_type !== "distributor" || !payload.distributor_id) delete payload.distributor_id;
      const { data } = await adminApi.put(`/inventory/movements/${detail.id}`, payload);
      toast.success("Movement updated");
      setEditOpen(false);
      setDetail(data);
      await load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally { setEditBusy(false); }
  };

  const finalizeMovement = async (m) => {
    if (!m || m.finalized) return;
    if (!window.confirm("Finalise this movement? After finalisation no more edits will be possible.")) return;
    setFinalizing(true);
    try {
      await adminApi.post(`/inventory/movements/${m.id}/finalize`);
      toast.success("Movement finalised");
      await load();
      setDetail((d) => (d && d.id === m.id ? { ...d, finalized: true } : d));
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally { setFinalizing(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-inventory-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="label-eyebrow text-gold">Stock movements</p>
          <h1 className="font-serif text-4xl text-bone mt-1">Inventory Ledger</h1>
          {lowStockOnly && (
            <button
              onClick={() => setLowStockOnly(false)}
              className="mt-3 inline-flex items-center gap-2 label-eyebrow text-rose-300 bg-burgundy/10 border border-burgundy/40 px-3 py-1.5 hover:bg-burgundy/15 transition-colors"
              data-testid="low-stock-filter-chip"
            >
              Showing low-stock only · <span className="opacity-60">clear ×</span>
            </button>
          )}
        </div>
        <button onClick={() => { setForm({ ...empty, movement_type: "stock_in" }); setOpen(true); }} className="btn-primary !py-3" data-testid="new-movement-btn">
          <Plus className="w-3.5 h-3.5" /> Record movement
        </button>
      </div>

      {/* Pack-size bifurcation cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="pack-size-summary">
        {[
          { key: "single", label: "Single units" },
          { key: "3-pack", label: "3-pack units" },
          { key: "10-pack", label: "10-pack units" },
        ].map(({ key, label }) => {
          const skus = products.filter((p) => (p.pack_size || "single") === key);
          const totalUnits = skus.reduce((s, p) => s + (p.stock_quantity || 0), 0);
          const skuCount = skus.length;
          return (
            <div key={key} className="bg-ink-900 border border-white/5 p-5" data-testid={`pack-summary-${key}`}>
              <p className="label-eyebrow text-gold">{label}</p>
              <p className="num-display text-3xl text-bone mt-2">{totalUnits.toLocaleString("en-IN")}</p>
              <p className="text-mist text-xs font-light mt-1">{skuCount} SKU{skuCount === 1 ? "" : "s"}</p>
            </div>
          );
        })}
      </div>

      {/* Current stock snapshot */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {products
          .filter((p) => !lowStockOnly || p.stock_quantity < p.low_stock_threshold)
          .map((p) => {
            const low = p.stock_quantity < p.low_stock_threshold;
            return (
              <button
                key={p.sku}
                type="button"
                onClick={() => {
                  setForm({ ...empty, sku: p.sku, movement_type: low ? "stock_in" : "stock_out", quantity: 0 });
                  setOpen(true);
                }}
                className={`text-left bg-ink-900 border p-5 transition-colors hover:border-gold/50 ${low ? "border-rose-300/30 hover:border-rose-300/60 border-t-[3px] border-t-rose-300" : "border-white/5"}`}
                data-testid={`stock-card-${p.sku}`}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-mono text-xs text-mist">{p.sku}</p>
                    <p className="text-bone font-light mt-1">{p.name}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className={`label-eyebrow ${low ? "text-rose-300" : "text-gold"}`}>{low ? "Low" : "Healthy"}</span>
                    <span className="label-eyebrow text-mist text-[9px]">{p.pack_size === "3-pack" ? "3-PACK" : p.pack_size === "10-pack" ? "10-PACK" : "SINGLE"}</span>
                  </div>
                </div>
                <p className={`num-display text-3xl mt-3 ${low ? "text-rose-300" : "text-bone"}`}>{p.stock_quantity.toLocaleString("en-IN")}</p>
                <p className="text-mist text-xs font-light">units · threshold {p.low_stock_threshold}</p>
                <p className={`label-eyebrow text-[10px] mt-3 ${low ? "text-rose-300" : "text-mist/60"}`}>
                  {low ? "Tap to restock →" : "Tap to record movement →"}
                </p>
              </button>
            );
          })}
        {lowStockOnly && products.every((p) => p.stock_quantity >= p.low_stock_threshold) && (
          <p className="text-mist italic font-light col-span-full">All SKUs are above their thresholds. 🌿</p>
        )}
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3">
        <span className="label-eyebrow text-mist">Filter:</span>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="bg-ink-900 border border-white/10 px-3 py-2 text-sm font-light text-bone outline-none focus:border-gold"
          data-testid="movement-filter-select"
        >
          <option value="">All SKUs</option>
          {products.map((p) => <option key={p.sku} value={p.sku}>{p.sku} · {p.name}</option>)}
        </select>
      </div>

      {/* Movements table */}
      <div className="bg-ink-900 border border-white/5 overflow-x-auto" data-testid="movements-table">
        <table className="w-full text-sm">
          <thead className="bg-ink-950">
            <tr className="text-left label-eyebrow text-mist">
              <th className="px-4 py-3">Timestamp</th>
              <th className="px-4 py-3">SKU</th>
              <th className="px-4 py-3">Pack</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Qty</th>
              <th className="px-4 py-3">Balance</th>
              <th className="px-4 py-3">Source</th>
              <th className="px-4 py-3">By</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((m) => {
              const t = MOVEMENT_TYPES.find((x) => x.value === m.movement_type);
              const locked = !!m.finalized;
              const prod = products.find((p) => p.sku === m.sku);
              const pack = prod?.pack_size || "single";
              const packLabel = pack === "3-pack" ? "3-PACK" : pack === "10-pack" ? "10-PACK" : "SINGLE";
              return (
                <tr key={m.id} className="border-t border-white/5 text-bone/90 font-light" data-testid={`movement-row-${m.id}`}>
                  <td className="px-4 py-3 text-mist text-xs">{new Date(m.created_at).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3 font-mono text-xs">{m.sku}</td>
                  <td className="px-4 py-3"><span className="label-eyebrow text-mist text-[10px]">{packLabel}</span></td>
                  <td className={`px-4 py-3 ${t?.accent || ""}`}>{t?.label || m.movement_type}</td>
                  <td className="px-4 py-3">{m.quantity}</td>
                  <td className="px-4 py-3 text-gold">{m.balance_after}</td>
                  <td className="px-4 py-3 text-mist text-xs">
                    {m.source_type}
                    {m.source_type === "distributor" && (m.distributor_name || distributorById[m.distributor_id])
                      ? ` · ${m.distributor_name || distributorById[m.distributor_id]}`
                      : m.reference
                      ? ` · ${m.reference}`
                      : ""}
                  </td>
                  <td className="px-4 py-3 text-mist text-xs">{m.created_by}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-2 justify-end">
                      {locked ? (
                        <span className="inline-flex items-center gap-1 label-eyebrow text-gold border border-gold/30 px-2 py-0.5 text-[10px]" data-testid={`movement-locked-${m.id}`}>
                          <Lock className="w-3 h-3" /> Final
                        </span>
                      ) : (
                        <button
                          onClick={() => openEditDialog(m)}
                          className="text-mist hover:text-gold p-1.5"
                          title="Modify"
                          data-testid={`modify-movement-${m.id}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button
                        onClick={() => setDetail(m)}
                        className="text-mist hover:text-gold p-1.5"
                        title="View log"
                        data-testid={`view-movement-${m.id}`}
                      >
                        <History className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-mist font-light italic">No movements yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6">
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-ink-900 border border-white/10 w-full max-w-xl p-8 space-y-4" data-testid="movement-form-dialog">
            <div className="flex justify-between items-center">
              <h3 className="font-serif text-2xl text-bone">Record stock movement</h3>
              <button type="button" onClick={() => setOpen(false)} className="text-mist hover:text-gold"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <select required value={form.sku} onChange={set("sku")} className="col-span-2 bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" data-testid="movement-sku-select">
                <option value="">— Select SKU —</option>
                {products.map((p) => <option key={p.sku} value={p.sku}>{p.sku} · {p.name} (stock {p.stock_quantity})</option>)}
              </select>
              <select value={form.movement_type} onChange={set("movement_type")} className="bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" data-testid="movement-type-select">
                {MOVEMENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <input required type="number" min="1" value={form.quantity} onChange={set("quantity")} placeholder="Quantity" className="bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" data-testid="movement-qty-input" />
              <select value={form.source_type} onChange={(e) => setForm({ ...form, source_type: e.target.value, distributor_id: "" })} className="bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" data-testid="movement-source-select">
                <option value="manufacturer">Manufacturer</option>
                <option value="warehouse_receipt">Warehouse receipt</option>
                <option value="customer">Customer</option>
                <option value="distributor">Distributor</option>
                <option value="return">Return</option>
                <option value="adjustment">Adjustment</option>
              </select>
              <select value={form.warehouse_id} onChange={set("warehouse_id")} className="bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold">
                <option value="">— Warehouse (optional) —</option>
                {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>

              {form.source_type === "distributor" ? (
                <div className="col-span-2 space-y-1.5">
                  <select
                    required
                    value={form.distributor_id}
                    onChange={set("distributor_id")}
                    className="w-full bg-ink-950 border border-gold/40 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold"
                    data-testid="movement-distributor-select"
                  >
                    <option value="">— Select registered distributor —</option>
                    {activeDistributors.map((d) => (
                      <option key={d.id} value={d.id}>{d.name} · {d.city}, {d.state}</option>
                    ))}
                  </select>
                  {activeDistributors.length === 0 && (
                    <p className="text-rose-300 text-xs font-light">
                      No active distributors. Add one from the Distributors page first.
                    </p>
                  )}
                </div>
              ) : null}

              <input value={form.reference} onChange={set("reference")} placeholder="Reference (PO / invoice)" className="col-span-2 bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" />
              <textarea value={form.notes} onChange={set("notes")} placeholder="Notes" rows={2} className="col-span-2 bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold resize-none" />
            </div>
            <div className="flex justify-end gap-3 pt-3">
              <button type="button" onClick={() => setOpen(false)} className="btn-ghost !py-3">Cancel</button>
              <button type="submit" disabled={busy} className="btn-primary !py-3" data-testid="save-movement-btn">{busy ? "Saving…" : "Record"}</button>
            </div>
          </form>
        </div>
      )}

      {/* Detail drawer with edit log */}
      {detail && !editOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6">
          <div onClick={(e) => e.stopPropagation()} className="bg-ink-900 border border-white/10 w-full max-w-xl p-8 max-h-[90vh] overflow-y-auto" data-testid="movement-detail-dialog">
            <div className="flex justify-between items-start">
              <div>
                <p className="label-eyebrow text-gold">Movement detail</p>
                <h3 className="font-serif text-2xl text-bone mt-1 font-mono">{detail.sku}</h3>
                <div className="flex items-center gap-2 mt-1 flex-wrap text-xs">
                  <span className={MOVEMENT_TYPES.find((x) => x.value === detail.movement_type)?.accent || "text-mist"}>
                    {MOVEMENT_TYPES.find((x) => x.value === detail.movement_type)?.label || detail.movement_type}
                  </span>
                  <span className="text-mist">· qty {detail.quantity}</span>
                  {detail.finalized && (
                    <span className="inline-flex items-center gap-1 label-eyebrow text-gold border border-gold/40 px-2 py-0.5" data-testid="movement-lock-pill">
                      <Lock className="w-3 h-3" /> Finalised
                    </span>
                  )}
                </div>
              </div>
              <button onClick={() => setDetail(null)} className="text-mist hover:text-gold"><X className="w-5 h-5" /></button>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <button
                onClick={() => openEditDialog(detail)}
                disabled={detail.finalized}
                className="label-eyebrow px-3 py-2 border border-gold/40 text-gold hover:bg-gold/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                data-testid="modify-movement-btn"
              >
                <Pencil className="w-3 h-3" /> Modify
              </button>
              <button
                onClick={() => finalizeMovement(detail)}
                disabled={detail.finalized || finalizing}
                className="label-eyebrow px-3 py-2 border border-emerald-400/40 text-emerald-400 hover:bg-emerald-400/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                data-testid="finalize-movement-btn"
              >
                <CheckCircle2 className="w-3 h-3" /> {finalizing ? "Finalising…" : "Finalise"}
              </button>
            </div>

            <dl className="mt-6 grid grid-cols-2 gap-3 text-sm font-light">
              <div><dt className="label-eyebrow text-mist">Source</dt><dd className="text-bone">{detail.source_type}</dd></div>
              <div><dt className="label-eyebrow text-mist">Reference</dt><dd className="text-bone">{detail.reference || "—"}</dd></div>
              <div><dt className="label-eyebrow text-mist">Distributor</dt><dd className="text-bone">{detail.distributor_name || "—"}</dd></div>
              <div><dt className="label-eyebrow text-mist">Balance after</dt><dd className="text-gold">{detail.balance_after}</dd></div>
              {detail.notes && <div className="col-span-2"><dt className="label-eyebrow text-mist">Notes</dt><dd className="text-bone">{detail.notes}</dd></div>}
            </dl>

            <div className="mt-6 pt-5 border-t border-white/5">
              <p className="label-eyebrow text-gold mb-3">Edit history ({(detail.edit_logs || []).length})</p>
              {(detail.edit_logs || []).length === 0 ? (
                <p className="text-mist text-xs font-light italic">No edits yet.</p>
              ) : (
                <ul className="space-y-3 max-h-64 overflow-y-auto pr-1">
                  {[...(detail.edit_logs || [])].reverse().map((log, i) => (
                    <li key={i} className="bg-ink-950 border border-white/5 p-3 text-xs font-light" data-testid={`movement-log-${i}`}>
                      <div className="flex justify-between text-mist">
                        <span className="label-eyebrow text-gold text-[10px]">{log.action}</span>
                        <span className="text-[10px]">{new Date(log.ts).toLocaleString("en-IN")} · {log.by}</span>
                      </div>
                      {log.changes && Object.keys(log.changes).length > 0 && (
                        <ul className="mt-2 space-y-1">
                          {Object.entries(log.changes).map(([field, val]) => (
                            <li key={field} className="text-bone/90 break-all">
                              <span className="text-mist">{field}:</span>{" "}
                              {typeof val === "object" && val !== null && "from" in val ? (
                                <>
                                  <span className="line-through text-rose-300/70">{JSON.stringify(val.from) ?? "—"}</span>
                                  <span className="text-mist mx-1">→</span>
                                  <span className="text-emerald-400">{JSON.stringify(val.to) ?? "—"}</span>
                                </>
                              ) : (
                                <span className="text-emerald-400">{JSON.stringify(val)}</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit movement modal */}
      {editOpen && detail && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-6">
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={saveEdit}
            className="bg-ink-900 border border-gold/40 w-full max-w-xl p-8 space-y-4 max-h-[90vh] overflow-y-auto"
            data-testid="edit-movement-dialog"
          >
            <div className="flex justify-between items-center">
              <div>
                <p className="label-eyebrow text-gold">Modify movement</p>
                <h3 className="font-serif text-2xl text-bone mt-1 font-mono">{detail.sku} · qty {detail.quantity}</h3>
              </div>
              <button type="button" onClick={() => setEditOpen(false)} className="text-mist hover:text-gold"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-mist text-xs font-light">
              For ledger integrity, quantity and type cannot be changed. All edits are logged.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <select
                value={editForm.source_type}
                onChange={(e) => setEditForm({ ...editForm, source_type: e.target.value, distributor_id: "" })}
                className="bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold"
                data-testid="edit-movement-source"
              >
                <option value="manufacturer">Manufacturer</option>
                <option value="warehouse_receipt">Warehouse receipt</option>
                <option value="customer">Customer</option>
                <option value="distributor">Distributor</option>
                <option value="return">Return</option>
                <option value="adjustment">Adjustment</option>
              </select>
              <select value={editForm.warehouse_id} onChange={editSet("warehouse_id")} className="bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold">
                <option value="">— Warehouse —</option>
                {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
              {editForm.source_type === "distributor" && (
                <select
                  required
                  value={editForm.distributor_id}
                  onChange={editSet("distributor_id")}
                  className="col-span-2 bg-ink-950 border border-gold/40 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold"
                  data-testid="edit-movement-distributor"
                >
                  <option value="">— Select registered distributor —</option>
                  {activeDistributors.map((d) => (
                    <option key={d.id} value={d.id}>{d.name} · {d.city}, {d.state}</option>
                  ))}
                </select>
              )}
              <input
                value={editForm.reference}
                onChange={editSet("reference")}
                placeholder="Reference (PO / invoice)"
                className="col-span-2 bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold"
                data-testid="edit-movement-reference"
              />
              <textarea
                value={editForm.notes}
                onChange={editSet("notes")}
                placeholder="Notes"
                rows={2}
                className="col-span-2 bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold resize-none"
                data-testid="edit-movement-notes"
              />
            </div>
            <div className="flex justify-end gap-3 pt-3">
              <button type="button" onClick={() => setEditOpen(false)} className="btn-ghost !py-3">Cancel</button>
              <button type="submit" disabled={editBusy} className="btn-primary !py-3" data-testid="save-edit-movement-btn">
                {editBusy ? "Saving…" : "Save changes"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
